import React, { useCallback, useState, useEffect, useRef } from "react"
import { useHistory } from "react-router-dom"
import Form from "@bappweb/form"
import Panel from "@bappweb/panel"
import TextField from "@bappweb/textfield"
import Button from "@bappweb/button"
import Image from "@bappweb/image"
import Captcha from "@smpu-web/nested/form/captcha"
import FormMessage from "@smpu-web/globalmodules/form-message/index"
import Page from "@smpu-web/globalmodules/page"
import { RegexpTypes, required } from "@bappweb/utils"
import {
  useGetTokenByTckn1Mutation,
  useGetCaptchaMutation,
  useRefreshCaptchaMutation,
  useStep1Mutation
} from "../../../api/services"
import "../assets/style.scss"
import "../../../../../assets/styles/style.scss"

type PropsType = {
  onError?: (event?: React.BaseSyntheticEvent, errorField?: Record<string, any>) => void
  onSubmit?: (event?: React.BaseSyntheticEvent, values?: Record<string, any>) => void
}

const DOCUMENT_ID_MAX_LENGTH = 36
const DOCUMENT_ID_MIN_LENGTH = 36
const DOCUMENT_ID_REGEX_PATTERN = /([A-Za-z0-9---]+|\d+)$/gi

const getQueryStringValue = (paramName, defaultValue) => {
  const queryParams = new URLSearchParams(window.location.search)
  if (queryParams){
    let retVal:any = queryParams.get(paramName)
    if (retVal){
      return retVal
    }
    return defaultValue
  }
}

function MainForm({ onError, onSubmit }: PropsType) {
  const history = useHistory()
  const refForm = useRef(null)

  const [documentId, setDocumentId] = useState("")
  const [documentNumber, setDocumentNumber] = useState("")

  const [captchaValues, setCaptchaValues] = useState({
    tokenSessionId: "",
    guid: "",
    captchaData: "",
    captcha: "",
    showCaptcha: false
  })

  const [error, setError] = useState<string | null>(null)
  const [isFetching, setIsFetching] = useState(false)
  const [getTokenByTckn1] = useGetTokenByTckn1Mutation()

  const getToken = useCallback((docId) => {
    if ((docId ?? "").length >= DOCUMENT_ID_MIN_LENGTH && docId.length <= DOCUMENT_ID_MAX_LENGTH && docId.match(DOCUMENT_ID_REGEX_PATTERN)) {
      setIsFetching(true)
      getTokenByTckn1(docId)
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setError("")
            setCaptchaValues({
              ...captchaValues,
              tokenSessionId: payload.tokenSessionId,
              guid: docId
            })
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }
  }, [getTokenByTckn1, captchaValues])

  const handleDocumentIdBlur = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const { value } = e.target
      if (value !== "" && captchaValues.tokenSessionId === ""){
        getToken(value)
      }
    },
    [getToken, captchaValues]
  )

  const handleChange = useCallback(
    (e) => {
      const name = e.target.name
      const value = e.target.value

      switch (name) {
        case "documentId":
          setError(null)
          setDocumentId(value)
          setCaptchaValues({
            ...captchaValues,
            captchaData: "",
            tokenSessionId: "",
            showCaptcha: false
          })

          // @ts-ignore TODO
          // Captcha alanı temizlendiğinde validasyon hatası alıyor. O nedenle bu şekilde alanı temizliyoruz.
          refForm.current?.setValue("captcha", "")
          break
        case "documentNumber":
          setError(null)
          setDocumentNumber(value)
          break
        case "captcha":
          setError(null)
          setCaptchaValues({
            ...captchaValues,
            captcha: value
          })
          break
        default:
          break
      }
    },
    [captchaValues]
  )

  const [getCaptcha] = useGetCaptchaMutation()
  useEffect(() => {
    const docId = getQueryStringValue("id", "")
    setDocumentId(docId)
    setDocumentNumber(getQueryStringValue("no", ""))
    getToken(docId)
  }, []) // eslint-disable-line

  useEffect(() => {
    if (captchaValues.captchaData === "" && captchaValues.tokenSessionId !== "") {
      setIsFetching(true)
      getCaptcha({
        guid: captchaValues.guid,
        TokenSessionId: captchaValues.tokenSessionId
      })
        .unwrap()
        .then((payload) => {
          if (!payload.IsError) {
            setCaptchaValues({
              ...captchaValues,
              captchaData: payload.captchaData,
              showCaptcha: true
            })
          } else {
            setError(payload.ErrorMessage ? payload.ErrorMessage : "")
          }
        })
        .finally(() => {
          setIsFetching(false)
        })
    }
  }, [getCaptcha, captchaValues])

  const [refreshCaptcha] = useRefreshCaptchaMutation()
  const handleCaptchaRefresh = useCallback(() => {
    setIsFetching(true)
    refreshCaptcha({
      guid: captchaValues.guid,
      TokenSessionId: captchaValues.tokenSessionId
    })
      .unwrap()
      .then((payload) => {
        if (!payload.IsError) {
          setCaptchaValues({
            ...captchaValues,
            captchaData: payload.captchaData
          })
        } else {
          setError(payload.ErrorMessage ? payload.ErrorMessage : "")
        }
      })
      .finally(() => {
        setIsFetching(false)
      })
  }, [captchaValues, refreshCaptcha])

  const [step1] = useStep1Mutation()

  const handleSubmit = useCallback(
    (e?: React.BaseSyntheticEvent, values?: Record<string, any>) => {
      if (documentId && documentId.match(DOCUMENT_ID_REGEX_PATTERN) && documentNumber &&  captchaValues.captcha !== "") {
        setIsFetching(true)
        step1({
          guid: documentId,
          no: documentNumber,
          captcha: captchaValues.captcha.toUpperCase(),
          TokenSessionId: captchaValues.tokenSessionId
        })
          .unwrap()
          .then((payload) => {
            if (!payload.IsError) {
              setError(null)
              history.push({
                pathname: "/result/success",
                state: payload
              })
            } else {
              setError(payload.ErrorMessage ? payload.ErrorMessage : "")
              window.scrollTo({
                top: 0
              })
              refreshCaptcha({
                guid: captchaValues.guid,
                TokenSessionId: captchaValues.tokenSessionId
              })
                .unwrap()
                .then((payload) => {
                  if (!payload.IsError) {
                    setCaptchaValues({
                      ...captchaValues,
                      captcha: "",
                      showCaptcha: true,
                      captchaData: payload.captchaData
                    })
                  } else {
                    setError(payload.ErrorMessage ? payload.ErrorMessage : "")
                  }
                })
                .finally(() => {
                  setIsFetching(false)
                })
            }
          })
          .finally(() => {
            setIsFetching(false)
          })
      }
    },
    [captchaValues, documentId, documentNumber, step1, refreshCaptcha, history]
  )

  return (
    <Page>
      <Form onSubmit={handleSubmit} onChange={handleChange} grid={false} ref={refForm}>
        <Panel hasFooter connected={false} loading={isFetching}>
          <FormMessage className={"form-msg-error"} error={error}></FormMessage>
          <Panel.Body>
          <Image path={"./img/common/akbank_logo.png"} className="landing-header-img" />
            <TextField
              type="text"
              name="documentId"
              label="Belge ID"
              maxLength={DOCUMENT_ID_MAX_LENGTH}
              onBlur={handleDocumentIdBlur}
              value={documentId}
              validations={[required("Lütfen belge ID giriniz.")]}
            />
            <TextField
              label="Belge Numarası"
              type="number"
              name="documentNumber"
              maxLength={10}
              value={documentNumber}
              validator={RegexpTypes.Numeric}
              validations={[required("Lütfen belge numarası giriniz.")]}
            />
            {captchaValues.showCaptcha && (
              <Captcha
                label="Güvenlik Kodu"
                id="captcha"
                type="text"
                name="captcha"
                captcha={captchaValues.captchaData}
                value={captchaValues.captcha}
                maxLength={4}
                maxRefreshCount={3}
                refreshClick={handleCaptchaRefresh}
                validations={[required("Lütfen güvenlik kodunu giriniz.")]}
              />
            )}
          </Panel.Body>
          <Panel.Footer>
            <Panel.RightFooter>
              <Button text="DEVAM" type="submit" disabled={isFetching} />
            </Panel.RightFooter>
          </Panel.Footer>
        </Panel>
      </Form>
    </Page>
  )
}

export default MainForm
